import {addSortToUrl, processFilters, processGetManyByReferenceFilter, processGetManyFilter, readJsonOrRefuse, refuseOnHttpError} from "./provider/defaults";
import {
    CreateParams,
    CreateResult,
    DataProvider,
    DeleteManyParams,
    DeleteParams,
    DeleteResult,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyReferenceParams,
    GetManyReferenceResult,
    GetManyResult,
    GetOneParams,
    GetOneResult,
    UpdateManyParams,
    UpdateManyResult,
    UpdateParams,
    UpdateResult
} from "react-admin";
import {read_cookie} from 'sfcookies';
import {apiUrl, hostUrl, UseUrlManagerCleanUrl} from "../components/utils/url-manager";
import { CustomerAccount } from "../types/entities";
import { addUrlToUri, removeUrlFromUri, restoreEntityString, cleanEntityString } from "./provider/defaults";

function cleanParams(data: any[]) : CustomerAccount {
    // console.log(data);
    return {
        id: data['id'],
        setupComplete: data['setupComplete'],
        type: data['type'],
        stripeId: data['stripeId'],
        billingAddress: data['billingAddress'],
        status: data['status'],
        accountHolder: cleanEntityString(data['accountHolder'], '/hq_api/customers/'),
        createdAt: data['createdAt'],
        updatedAt: data['updatedAt'],
        subscriptions: removeUrlFromUri(data['subscriptions'], '/hq_api/subscriptions/'),
        subscriptionInvoices: removeUrlFromUri(data['subscriptionInvoices'], '/hq_api/subscription_invoices/'),
        companies: removeUrlFromUri(data['companies'], '/hq_api/companies/'),
        trialEndDate: data['trialEndDate'],
        trialCompleted: data['trialCompleted'],
        credits: data['credits'],
        emailFinders: removeUrlFromUri(data['emailFinders'], '/hq_api/email_finders/'),
        mysteryShoppers: removeUrlFromUri(data['mysteryShoppers'], '/hq_api/mystery_shoppers/'),
        complianceSniffers: removeUrlFromUri(data['complianceSniffers'], '/hq_api/compliance_sniffers/'),
    }
}

const customerAccountDataProvider: DataProvider = {

    getList: async (resource: string, params: GetListParams) : Promise<GetListResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: CustomerAccount[], total: number } => {
                let responseData: { data: CustomerAccount[], total: number } = {
                    data: [],
                    total: 0
                };
                // console.log(data);
                if (Array.isArray(data['hydra:member'])) {
                    let Operator: any = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < Operator.length; i++) {
                        let operator: any = Operator[i];
                        responseData.data.push(cleanParams(operator))
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error): Promise<GetListResult> => {
                console.log(error);
                return Promise.reject(error)
            });
        //return Promise.reject();
    },
    // get a single record by id
    getOne: async (resource: string, params: GetOneParams) : Promise<GetOneResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: CustomerAccount } => {
                // console.log(data);
                let responseData: { data: CustomerAccount|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error): Promise<GetOneResult> => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // get a list of records based on an array of ids
    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        url = processGetManyFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: CustomerAccount[], total: number } => {
                let responseData: { data: CustomerAccount[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let operators: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < operators.length; i++) {
                        let operator: any[] = operators[i];
                        responseData.data.push(cleanParams(operator));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error): Promise<GetManyResult> => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // get the records referenced to another record, e.g. comments for a post
    getManyReference: async (resource: string, params: GetManyReferenceParams) : Promise<GetManyReferenceResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processGetManyByReferenceFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: CustomerAccount[], total: number } => {
                let responseData: { data: CustomerAccount[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let operators: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < operators.length; i++) {
                        let operator: any[] = operators[i];
                        responseData.data.push(cleanParams(operator));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error): Promise<GetManyReferenceResult> => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // create a record
    create: async (resource: string, params: CreateParams) : Promise<CreateResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        url = UseUrlManagerCleanUrl({ url: url });

        const today = new Date();
        let hours: any = today.getHours();
        if (hours < 10) {
            hours = '0' + hours;
        }
        let minutes: any = today.getMinutes();
        if (minutes < 10) {
            minutes = '0' + minutes;
        }
        let seconds: any = today.getSeconds();
        if (seconds < 10) {
            seconds = '0' + seconds;
        }

        params.data.createdAt = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate() + ' ' +
            hours + ':' + minutes + ':' + seconds;
        params.data.accountHolder = restoreEntityString(params.data.accountHolder, 'hq_api/customers/');
        params.data.companies = addUrlToUri(params.data.companies, '/hq_api/companies/');
        let billingAddress = params.data.billingAddress;
        let newBillingAddress = [];
        if (Array.isArray(billingAddress)) {
            for (let i: number = 0; i < billingAddress.length; i++) {
                let address = billingAddress[i];
                let addr: {
                    lineOne: string,
                    lineTwo: string,
                    city: string,
                    county: string,
                    country: string,
                    postcode: string
                } = {
                    lineOne: address.lineOne ?? '',
                    lineTwo: address.lineTwo ?? '',
                    city: address.city ?? '',
                    county: address.county ?? '',
                    country: address.country ?? '',
                    postcode: address.postcode ?? '',
                }
                newBillingAddress.push(addr);
            }
        }
        params.data.billingAddress = newBillingAddress;
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response):  Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: CustomerAccount } => {
                let responseData: { data: CustomerAccount|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error): Promise<CreateResult> => {
                console.log(error);
                return Promise.reject(error);
            });

    },
    // update a record based on a patch
    update: async (resource: string, params: UpdateParams) : Promise<UpdateResult> => {
        let token: string = read_cookie('auth');
         let url: string = apiUrl + resource + '/' + params.data.id;
         url = UseUrlManagerCleanUrl({ url: url });

        const today = new Date();
        let hours: any = today.getHours();
        if (hours < 10) {
            hours = '0' + hours;
        }
        let minutes: any = today.getMinutes();
        if (minutes < 10) {
            minutes = '0' + minutes;
        }
        let seconds: any = today.getSeconds();
        if (seconds < 10) {
            seconds = '0' + seconds;
        }

        params.data.updatedAt = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate() + ' ' +
            hours + ':' + minutes + ':' + seconds;
        params.data.accountHolder = restoreEntityString(params.data.accountHolder, 'hq_api/customers/');
        params.data.subscriptionInvoices = addUrlToUri(params.data.subscriptionInvoices, '/hq_api/subscription_invoices/');
        params.data.subscriptions = addUrlToUri(params.data.subscriptions, '/hq_api/subscriptions/');
        params.data.companies = addUrlToUri(params.data.companies, '/hq_api/companies/');
        let billingAddress = params.data.billingAddress;
        let newBillingAddress = [];
        if (Array.isArray(billingAddress)) {
            for (let i: number = 0; i < billingAddress.length; i++) {
                let address = billingAddress[i];
                let t = JSON.stringify(address);
                let addr = {
                    lineOne: address.lineOne ?? '',
                    lineTwo: address.lineTwo ?? '',
                    city: address.city ?? '',
                    county: address.county ?? '',
                    country: address.country ?? '',
                    postcode: address.postcode ?? '',
                    defaultAddress: address.defaultAddress ?? false
                }
                newBillingAddress.push(addr);
            }
        }
        params.data.billingAddress = newBillingAddress;
        params.data.emailFinders = addUrlToUri(params.data.emailFinders, '/hq_api/email_finders/');
        params.data.mysteryShoppers = addUrlToUri(params.data.mysteryShoppers, '/hq_api/mystery_shoppers/');
        params.data.complianceSniffers = addUrlToUri(params.data.complianceSniffers, '/hq_api/compliance_sniffers/');

        const request: Request = new Request(url, {
            method: 'PATCH',
            body: JSON.stringify(params['data']),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: CustomerAccount } => {
                let responseData: { data: CustomerAccount|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error): Promise<UpdateResult> => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // update a list of records based on an array of ids and a common patch
    updateMany: async (resource: string, params: UpdateManyParams) : Promise<UpdateManyResult> => {
        return Promise.reject();
    },
    // delete a record by id
    delete: async (resource: string, params: DeleteParams) : Promise<DeleteResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'DELETE',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<{ data: any }> => {
                return refuseOnHttpError(response)
                    .then((): { data: any } => ({ data: params }));
            })
            .catch( (error: Error): Promise<any> => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // delete a list of records based on an array of ids
    deleteMany: async (resource: string, params: DeleteManyParams) : Promise<any> => {
        return Promise.reject();
    },
    getProfileData: (params: any): Promise<void> => {
        return Promise.resolve();
    }
}


export default customerAccountDataProvider;